import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import {
    BarChart3,
    Car,
    CheckCircle2,
    Clock3,
    Download,
    Eye,
    Factory,
    Gauge,
    Inbox,
    LayoutGrid,
    Palette,
    Search,
    Table2,
    Wallet,
    Wrench,
    XCircle,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { httpClient } from '../../common/httpclient';
import ReportFilterBar from '../../component/report/ReportFilterBar';
import DialogInfo from '../../component/dialog/DialogInfo';
import AnimatedNumber from '../../component/common/AnimatedNumber';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import DefaultTable from '../../component/table/DefaultTable';
import { KvHero, KvStripItem, UsageKpi } from '../../component/report/KvKpis';
import usePermissions from '../../hooks/usePermissions';
import VehicleViewDrawer from '../vehicles/View';
import '../reports/reports.css';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';
import MobileReport, { MobileReportBody, useMobileRegenerate } from '../../component/report/MobileReport';
import { AreaLine, ChartCard, DonutBlock, HBarsLimited } from '../../component/report/MobileSalesSections';
import { KvCard, PagedRows, RowValue, StatTiles } from '../../component/report/MobileListSections';
import { KitEmptyNote } from '../../component/common/MobileDashboardKit';
import { KitPill, SecH } from '../../component/common/MobileKit';
import { KitSearch, KitSelect, KitSeg } from '../../component/common/MobileKitExtras';

/**
 * Cobertura de dados x protótipo (`prototipo-relatorio-veiculos.html`):
 *
 * A tela combina 4 endpoints já existentes (nenhum novo criado nesta
 * tarefa):
 *  - `POST /chart/vehicle` (`report/vehicle.go`) — estatísticas gerais e os
 *    gráficos de tipo/fabricante/ano/cor/mês/status.
 *  - `POST /chart/vehicle/values` (`report/vehicle.go`) — valor de
 *    produtos/serviços por veículo ATIVO que já teve algum item de OS.
 *  - `GET /vehicles/statistics` (`vehicle/statistics.go`) — usa o mesmo
 *    endpoint do Dashboard de Veículos só para reaproveitar
 *    `cars_with_most_services`/`cars_with_most_expensive_services` (única
 *    fonte que já traz placa por veículo).
 *  - `GET /vehicles` (`vehicle/getter.go`) — roster completo de veículos
 *    ATIVOS (placa/cliente/km/fabricante/modelo/cor), usado na aba
 *    Detalhado e nos filtros de Marca/Busca.
 *
 * Lacunas reais (não inventadas — documentadas linha a linha abaixo):
 *  1. Nenhum endpoint expõe "quantidade de OS" nem "data da última OS" por
 *     veículo (só o VALOR somado de produtos/serviços). Por isso a coluna
 *     "Qtd. OS"/"Última OS" da aba Detalhado do protótipo e o painel
 *     "Veículos sem visita recente" (>90 dias) da aba Visão Geral não têm
 *     como ser preenchidos sem uma nova consulta no backend (precisaria de
 *     `MAX(service_orders.date)`/`COUNT(service_orders.id)` por veículo).
 *  2. `GET /vehicles` só retorna veículos com `active = true` — não há
 *     endpoint que devolva o roster completo de INATIVOS com
 *     placa/cliente/km. A aba Detalhado mostra sempre "Ativo" como status por
 *     causa dessa limitação (não há filtro de status na tela).
 *  3. `cars_with_most_services`/`cars_with_most_expensive_services` (usados
 *     nos rankings Top 5 da aba Gráficos) só consideram
 *     `vehicle_type = 'car'` — motos e outros tipos ficam de fora dessas
 *     duas tabelas especificamente (limitação da query em
 *     `vehicle/repository.go`).
 *  4. Não existe "modelo mais comum" (só fabricante) nem uma faixa de
 *     "OS por veículo" (0 / 1-3 / 4-8 / 9+) — ambos exigiriam contagem de OS
 *     por veículo (mesma lacuna do item 1). A KPI "Marca/modelo mais comum"
 *     do protótipo virou só "Marca mais comum" e o gráfico de faixas de OS
 *     foi substituído por "Veículos por cor" (dado real já disponível).
 *  5. O período (Início/Fim) é enviado para `/chart/vehicle` só por
 *     compatibilidade — o handler (`report/vehicle.go`) nunca lê o corpo da
 *     requisição, então essas datas não filtram nada hoje (mesmo
 *     comportamento decorativo que a tela já tinha antes do redesign).
 *  6. Bug de dados pré-existente encontrado ao validar esta tela (não
 *     corrigido aqui — é backend/dado, fora do escopo visual): a query de
 *     `GetVehicleStatistics` (`report/repository.go`) reconhece
 *     `vehicle_type IN ('carro','moto')` (português), mas o cadastro real
 *     grava em inglês (`car`/`motorcycle`/`bicycle`, ver `VEHICLE_TYPES` em
 *     `pages/vehicles/Form.js`) — por isso `total_cars`/`total_motorcycles`
 *     sempre voltam 0 e `total_other_vehicles` soma tudo. A mesma
 *     inconsistência de dados de `fab_year` também gera um `average_age`
 *     visivelmente errado (ex.: "388,2 anos" com a massa de dados local).
 *     A tela exibe o valor exatamente como a API devolve, sem arredondar
 *     nem esconder o problema.
 *
 * Nenhuma extensão de backend foi implementada nesta tarefa — é só
 * frontend, reaproveitando dado real já existente.
 */

const MONTH_ABBR_LABELS = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

const parseBrl = (v) => {
    if (typeof v === 'number') return v;
    const n = Number(String(v ?? '0').replace(/[^\d,.-]/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
};

const formatBrl = (v) =>
    parseBrl(v).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

const normalizeText = (s) =>
    String(s ?? '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .trim()
        .toLowerCase();

const fmtKm = (value) => `${Math.round(Number(value) || 0).toLocaleString('pt-BR')} km`;

const fmtYear = (row) => {
    if (row.FabYear && row.ModYear && row.FabYear !== row.ModYear) {
        return `${row.FabYear}/${row.ModYear}`;
    }
    return row.ModYear || row.FabYear || '—';
};

// Traduz o código de `vehicle_type` (`car`/`motorcycle`/`bicycle`, mesmos
// valores de `VEHICLE_TYPES` em `pages/vehicles/Form.js`) para o rótulo em
// português do design system. `type_chart` (`/chart/vehicle`) tenta traduzir
// no backend (`report/repository.go` `GetVehiclesByType`), mas a query só
// reconhece os valores em português ('carro'/'moto') — como o cadastro real
// grava em inglês, o backend acaba devolvendo o código cru sem tradução;
// aplicado aqui só como formatação de rótulo, sem alterar nenhum dado.
const VEHICLE_TYPE_LABELS = {
    car: 'Carro',
    carro: 'Carro',
    motorcycle: 'Moto',
    moto: 'Moto',
    bicycle: 'Bicicleta',
    bicicleta: 'Bicicleta',
    truck: 'Caminhão',
};
const vehicleTypeLabel = (type) => {
    const key = normalizeText(type);
    if (!key) return 'Outros';
    return VEHICLE_TYPE_LABELS[key] || (type.charAt(0).toUpperCase() + type.slice(1));
};

/**
 * DESIGN-SYSTEM.md §6.1 "report-toolbar" micro-label — uppercase, static,
 * rendered outside the field (not `InputLabel`). Mesmo padrão local já usado
 * por `CustomerReport.js`/`SalesReport.js`/`pages/vehicles/List.js`.
 */
const TbLabel = ({ children }) => (
    <Typography sx={{ fontSize: '10.5px', lineHeight: '13px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '6px' }}>
        {children}
    </Typography>
);

const TB_FIELD_RADIUS = '9px';
const selectSx = { height: 40, fontSize: '13px', borderRadius: TB_FIELD_RADIUS };
const textFieldSx = {
    minWidth: 220,
    '& .MuiOutlinedInput-root': {
        height: 40,
        fontSize: '13px',
        borderRadius: TB_FIELD_RADIUS,
        backgroundColor: 'background.subtle',
    },
};

// Hero de frota (`.kv-hero`): cadastrados + sparkline | ativos + % + barra ativos/inativos.
// Sem delta vs. período anterior: a API não devolve essa variação.
function FleetHero({ total, active, inactive, activePct, growth, loading }) {
    return (
        <KvHero
            loading={loading}
            main={{
                icon: <Car size={17} strokeWidth={2} />,
                iconBg: 'info.bg',
                iconFg: 'primary.main',
                label: 'Veículos cadastrados',
                value: <AnimatedNumber value={total} />,
                sub: growth.some((v) => v > 0) ? 'Cadastros por mês no ano corrente' : 'Frota total cadastrada',
                growth,
            }}
            side={{
                title: 'Distribuição ativos x inativos',
                icon: <CheckCircle2 size={17} strokeWidth={2} />,
                iconBg: 'success.bg',
                iconFg: 'success.main',
                label: 'Veículos ativos',
                value: <AnimatedNumber value={active} />,
                pct: <><AnimatedNumber value={activePct} format={String} />%</>,
                pctTone: 'success',
                barPct: total > 0 ? (active / total) * 100 : 0,
                barBg: 'warning.bg',
                barFg: 'success.main',
                legend: [
                    { color: 'success.main', text: `${active.toLocaleString('pt-BR')} ativos` },
                    { color: 'warning.main', text: `${inactive.toLocaleString('pt-BR')} inativos` },
                ],
            }}
        />
    );
}

// `.stat-tile` — ícone circular + valor grande + legenda.
function StatTile({ icon, iconBg, iconFg, value, label, loading }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1, p: '16px 8px', borderRadius: '12px', bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ width: 42, height: 42, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: iconBg, color: iconFg }}>
                {icon}
            </Box>
            {loading ? (
                <Skeleton variant="text" width={40} height={30} />
            ) : (
                <Typography sx={{ fontSize: '24px', fontWeight: 800, letterSpacing: '-0.02em', color: 'text.primary' }}>{value}</Typography>
            )}
            <Typography sx={{ fontSize: '10.5px', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</Typography>
        </Box>
    );
}

// `.metric-row` — ícone quadrado tintado + rótulo pequeno + valor em negrito.
function MetricRow({ icon, iconBg, iconFg, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: '11px 10px', borderRadius: '10px', '&:hover': { bgcolor: 'background.subtle' } }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, bgcolor: iconBg, color: iconFg }}>
                {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: '10.5px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.disabled' }}>{label}</Typography>
                <Typography sx={{ fontSize: '14.5px', fontWeight: 800, color: 'text.primary' }}>{value}</Typography>
            </Box>
        </Box>
    );
}

// `.chip-count` — contador arredondado usado nas colunas de quantidade.
function CountChip({ value }) {
    return (
        <Chip
            label={value}
            size="small"
            sx={{ minWidth: 30, height: 22, fontWeight: 800, fontSize: '12px', bgcolor: 'info.bg', color: 'primary.main' }}
        />
    );
}

// `.pill` — dot + rótulo, usado para status/severidade.
const PILL_TONE_SX = {
    green: { bgcolor: 'success.bg', color: 'success.main' },
    amber: { bgcolor: 'warning.bg', color: 'warning.main' },
    red: { bgcolor: 'error.bg', color: 'error.main' },
    blue: { bgcolor: 'info.bg', color: 'primary.main' },
    grey: { bgcolor: 'background.subtle', color: 'text.secondary' },
};
function Pill({ label, tone = 'grey' }) {
    const toneSx = PILL_TONE_SX[tone] || PILL_TONE_SX.grey;
    return (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 700, padding: '4px 10px', borderRadius: '20px', ...toneSx }}>
            <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'currentColor' }} />
            {label}
        </Box>
    );
}

// DESIGN-SYSTEM.md §5.11 empty state (variante compacta).
function EmptyMiniState({ icon, title, subtitle }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, py: 3, textAlign: 'center' }}>
            <Box sx={{ width: 56, height: 56, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                {icon}
            </Box>
            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.secondary' }}>{title}</Typography>
            <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>{subtitle}</Typography>
        </Box>
    );
}

// `.legend-item` — dot + rótulo + valor em negrito, usado sob os donuts.
function LegendRow({ color, label, value }) {
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '11.5px', color: 'text.secondary', width: '100%' }}>
            <Box sx={{ width: 9, height: 9, borderRadius: '3px', bgcolor: color, flexShrink: 0 }} />
            <Typography component="span" sx={{ fontSize: '11.5px', color: 'text.secondary' }}>{label}</Typography>
            <Typography component="span" sx={{ fontSize: '11.5px', fontWeight: 700, color: 'text.primary', ml: 'auto' }}>{value}</Typography>
        </Box>
    );
}

// Painel de gráfico/card genérico (`.panel-head`).
function ChartPanel({ title, subtitle, loading, children }) {
    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ p: 2.25, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ mb: 2 }}>
                    {loading ? (
                        <>
                            <Skeleton variant="text" width={160} height={20} />
                            <Skeleton variant="text" width={200} height={14} />
                        </>
                    ) : (
                        <>
                            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>{title}</Typography>
                            {subtitle ? (
                                <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px' }}>{subtitle}</Typography>
                            ) : null}
                        </>
                    )}
                </Box>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                    {children}
                </Box>
            </CardContent>
        </Card>
    );
}

// Donut (PieChart real via @mui/x-charts) com overlay central + legenda.
function DonutPanel({ title, subtitle, data, centerValue, centerCaption, loading, emptyTitle, emptySubtitle }) {
    const hasData = data.some((item) => item.value > 0);
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, py: 1 }}>
                    <Skeleton variant="circular" width={128} height={128} />
                    <Skeleton variant="text" width="80%" height={16} />
                    <Skeleton variant="text" width="70%" height={16} />
                </Box>
            ) : hasData ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ position: 'relative', width: 128, height: 128 }}>
                        <PieChart
                            series={[{
                                data,
                                innerRadius: 38,
                                outerRadius: 64,
                                paddingAngle: 2,
                                cornerRadius: 3,
                            }]}
                            height={128}
                            width={128}
                            slotProps={{ legend: { hidden: true } }}
                            tooltip={{ trigger: 'item' }}
                            sx={{ '& .MuiChartsLegend-root': { display: 'none' } }}
                        />
                        <Box sx={{ position: 'absolute', inset: 18, borderRadius: '50%', bgcolor: 'background.paper', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                            <Typography sx={{ fontSize: '18px', fontWeight: 800, color: 'text.primary', lineHeight: 1.1 }}>{centerValue}</Typography>
                            <Typography sx={{ fontSize: '9.5px', color: 'text.disabled' }}>{centerCaption}</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, width: '100%' }}>
                        {data.map((item, index) => (
                            <LegendRow key={`${item.label}-${index}`} color={item.color} label={item.label} value={item.displayValue ?? item.value} />
                        ))}
                    </Box>
                </Box>
            ) : (
                <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title={emptyTitle} subtitle={emptySubtitle} />
            )}
        </ChartPanel>
    );
}

// Gráfico de linha/área — DESIGN-SYSTEM.md §5.9.
function AreaLineChart({ dataset, xKey, yKey, gradientId, title, subtitle, loading, height = 260, valueSuffix = '' }) {
    const theme = useTheme();
    const hasData = Array.isArray(dataset) && dataset.some((d) => Number(d[yKey]) > 0);
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            <Box sx={{ height, width: '100%' }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={height - 20} />
                ) : hasData ? (
                    <LineChart
                        dataset={dataset}
                        height={height - 20}
                        margin={{ top: 16, right: 16, bottom: 24, left: 8 }}
                        hideLegend
                        grid={{ horizontal: true }}
                        xAxis={[{
                            scaleType: 'point',
                            dataKey: xKey,
                            disableLine: true,
                            disableTicks: true,
                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                        }]}
                        yAxis={[{ disableLine: true, disableTicks: true }]}
                        series={[{
                            dataKey: yKey,
                            label: title,
                            color: theme.palette.primary.main,
                            area: true,
                            showMark: dataset.length <= 31,
                            valueFormatter: (value) => `${Number(value || 0).toLocaleString('pt-BR')}${valueSuffix}`,
                        }]}
                        sx={{
                            '& .MuiChartsGrid-line': { stroke: theme.palette.divider },
                            '& .MuiAreaElement-root': { fill: `url(#${gradientId})` },
                            '& .MuiMarkElement-root': { fill: theme.palette.background.paper, stroke: theme.palette.primary.main, strokeWidth: 2 },
                        }}
                    >
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                                <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                    </LineChart>
                ) : (
                    <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem veículos cadastrados no ano" />
                )}
            </Box>
        </ChartPanel>
    );
}

// Gráfico de barras genérico (contagem por categoria).
function CategoryBarChart({ data, xKey, title, subtitle, loading, height = 240, valueSuffix = ' veículo(s)' }) {
    const theme = useTheme();
    return (
        <ChartPanel title={title} subtitle={subtitle} loading={loading}>
            <Box sx={{ height, width: '100%' }}>
                {loading ? (
                    <Skeleton variant="rounded" width="100%" height={height - 20} />
                ) : data && data.length > 0 ? (
                    <BarChart
                        dataset={data}
                        height={height - 20}
                        margin={{ top: 16, right: 8, bottom: 24, left: 8 }}
                        borderRadius={6}
                        hideLegend
                        xAxis={[{
                            scaleType: 'band',
                            dataKey: xKey,
                            categoryGapRatio: 0.5,
                            disableLine: true,
                            disableTicks: true,
                            tickLabelStyle: { fontSize: 10.5, fontWeight: 600, fill: theme.palette.text.disabled },
                        }]}
                        yAxis={[{ disableLine: true, disableTicks: true }]}
                        series={[{
                            dataKey: 'count',
                            color: theme.palette.primary.main,
                            valueFormatter: (value) => `${value}${valueSuffix}`,
                        }]}
                        sx={{ '& .MuiChartsGrid-line': { stroke: theme.palette.divider } }}
                    />
                ) : (
                    <EmptyMiniState icon={<BarChart3 size={22} strokeWidth={2} />} title="Nenhum dado disponível" subtitle="Sem veículos cadastrados" />
                )}
            </Box>
        </ChartPanel>
    );
}

// `MiniTable` — tabela compacta sem paginação/busca própria, mesmo padrão de
// `CustomerReport.js` (tabelas secundárias pequenas e não paginadas no
// protótipo).
const miniHeadCellSx = { py: 1.25, px: 1.5, fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'text.disabled', borderBottom: '1px solid', borderColor: 'divider' };
const miniBodyCellSx = { py: 1.25, px: 1.5, fontSize: '12.5px', color: 'text.secondary', borderBottom: '1px solid', borderColor: 'divider' };
function MiniTable({ columns, rows, renderRow, maxHeight = 320, emptyTitle, emptySubtitle }) {
    if (!rows || rows.length === 0) {
        return <EmptyMiniState icon={<Inbox size={22} strokeWidth={2} />} title={emptyTitle || 'Nenhum registro'} subtitle={emptySubtitle || 'Sem dados para exibir'} />;
    }
    return (
        <TableContainer sx={{ maxHeight, overflow: 'auto' }}>
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        {columns.map((col) => (
                            <TableCell key={col.key} align={col.align || 'left'} sx={{ ...miniHeadCellSx, bgcolor: 'background.paper' }}>
                                {col.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row, index) => renderRow(row, index))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

export default function VehicleReport() {
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [vehicleValues, setVehicleValues] = useState([]);
    const [extraStats, setExtraStats] = useState(null);
    const [vehicleList, setVehicleList] = useState([]);
    const [activeTab, setActiveTab] = useState(0);
    const [startDate, setStartDate] = useState(dayjs().startOf('month'));
    const [endDate, setEndDate] = useState(dayjs().endOf('month'));
    const [manufacturerFilter, setManufacturerFilter] = useState('');
    const [search, setSearch] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMessage, setDialogMessage] = useState('');
    const [viewVehicleId, setViewVehicleId] = useState(null);
    const [openViewDrawer, setOpenViewDrawer] = useState(false);

    // Filtros (mobile): volta aos valores padrão sem gerar o relatório.
    const handleClearFilters = () => {
        setStartDate(dayjs().startOf('month'));
        setEndDate(dayjs().endOf('month'));
    };

    const fetchReportData = async () => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        setLoading(true);
        try {
            const [chartRes, valuesRes, statsRes, listRes] = await Promise.all([
                httpClient.post('/chart/vehicle', {
                    start_date: startDate.format('DD/MM/YYYY'),
                    end_date: endDate.format('DD/MM/YYYY'),
                }),
                httpClient.post('/chart/vehicle/values'),
                httpClient.get('/vehicles/statistics'),
                httpClient.get('/vehicles'),
            ]);

            setReportData(chartRes.data);
            setVehicleValues(Array.isArray(valuesRes.data) ? valuesRes.data : []);
            setExtraStats(statsRes.data || null);
            // `VeichleResponse` (Go) não tem tag JSON — o campo vem como `ID`
            // (maiúsculo), não `id`. `DefaultTable` usa `row.id` como key de
            // linha (mesma expectativa de `pages/vehicles/List.js`, que
            // consome o mesmo endpoint); normalizado aqui só para a key do
            // React, sem alterar nenhum dado exibido.
            const list = Array.isArray(listRes.data) ? listRes.data : [];
            setVehicleList(list.map((v) => ({ ...v, id: v.ID })));
        } catch (error) {
            console.error('Erro ao buscar dados do relatório:', error);
            setDialogMessage('Erro ao gerar relatório. Tente novamente.');
            setOpenDialog(true);
        } finally {
            setLoading(false);
        }
    };

    const handleExport = () => {
        if (!startDate || !endDate) {
            setDialogMessage('Por favor, selecione as datas de início e fim.');
            setOpenDialog(true);
            return;
        }

        const url = `/chart/vehicles/export?start_date=${startDate.format('YYYY-MM-DD')}&end_date=${endDate.format('YYYY-MM-DD')}`;
        window.open(url, '_blank');
    };

    const handleViewVehicle = (row) => {
        setViewVehicleId(row.ID);
        setOpenViewDrawer(true);
    };

    const statistics = reportData?.statistics || {};
    const totalVehicles = statistics.total_vehicles || 0;
    const activeVehicles = statistics.active_vehicles || 0;
    const inactiveVehicles = statistics.inactive_vehicles || 0;
    const activePct = totalVehicles > 0 ? Math.round((activeVehicles / totalVehicles) * 100) : 0;

    // "Veículos atendidos" — substitui a KPI "Média de OS por veículo" do
    // protótipo: nenhum endpoint devolve contagem de OS por veículo (só o
    // valor somado em `/chart/vehicle/values`), então em vez de inventar uma
    // média usamos a contagem real de veículos ativos com pelo menos um item
    // de produto/serviço lançado em OS.
    const attendedVehiclesCount = vehicleValues.length;

    // % do fabricante mais popular sobre o total — cruza `most_popular_manufacturer`
    // (statistics) com `manufacturer_chart` (mesma resposta), sem chamada nova.
    const manufacturerChart = useMemo(() => reportData?.manufacturer_chart || [], [reportData]);
    const mostPopularManufacturerPct = useMemo(() => {
        if (!statistics.most_popular_manufacturer || totalVehicles === 0) return null;
        const match = manufacturerChart.find((m) => normalizeText(m.label) === normalizeText(statistics.most_popular_manufacturer));
        if (!match) return null;
        return Math.round((match.value / totalVehicles) * 100);
    }, [manufacturerChart, statistics.most_popular_manufacturer, totalVehicles]);

    // Ticket médio de OS por veículo — média do valor total (produtos +
    // serviços) entre os veículos que aparecem em `/chart/vehicle/values`
    // (ou seja, que já tiveram OS lançada). Derivado 100% de campo já
    // devolvido pela API, sem endpoint novo.
    const averageTicketPerVehicle = useMemo(() => {
        if (vehicleValues.length === 0) return 0;
        const total = vehicleValues.reduce((sum, v) => sum + parseBrl(v.total_products_value) + parseBrl(v.total_services_value), 0);
        return total / vehicleValues.length;
    }, [vehicleValues]);

    // Crescimento da frota cadastrada — `monthly_chart` só traz meses com
    // cadastro (>0); preenchendo os 12 meses do ano corrente com zero para o
    // gráfico de área não ficar "andando torto".
    const monthlyGrowthData = useMemo(() => {
        const raw = reportData?.monthly_chart || [];
        const values = new Array(12).fill(0);
        raw.forEach((item) => {
            const idx = Number(item.x) - 1;
            if (idx >= 0 && idx < 12) values[idx] = item.y;
        });
        return MONTH_ABBR_LABELS.map((label, index) => ({ month: label, total: values[index] }));
    }, [reportData]);

    const typeChartData = useMemo(() => {
        const raw = reportData?.type_chart || [];
        return raw.map((item, index) => ({ id: index, label: vehicleTypeLabel(item.label), value: item.value, color: item.color }));
    }, [reportData]);

    const statusChartData = useMemo(() => {
        const raw = reportData?.status_chart || [];
        return raw.map((item, index) => ({
            id: index,
            label: item.label,
            value: item.value,
            color: item.label === 'Ativo' ? theme.palette.success.main : theme.palette.error.main,
        }));
    }, [reportData, theme]);

    const topManufacturersBarData = useMemo(() => manufacturerChart.slice(0, 5).map((item) => ({ label: item.label, count: item.value })), [manufacturerChart]);

    // Substitui o gráfico "OS por faixas" do protótipo (0/1-3/4-8/9+ OS),
    // que exigiria contagem de OS por veículo (lacuna documentada no topo do
    // arquivo). "Veículos por cor" usa dado real já devolvido pela API.
    const colorChartBarData = useMemo(() => (reportData?.color_chart || []).slice(0, 5).map((item) => ({ label: item.label, count: item.value })), [reportData]);

    // "Veículos com menor movimentação" — substitui o painel "sem visita
    // recente" (>90 dias) do protótipo: não há data de última OS por
    // veículo em nenhum endpoint hoje, então em vez de fabricar uma
    // contagem de dias, mostramos os veículos atendidos com MENOR valor de
    // produtos/serviços somado (ordem crescente), um indicador real e
    // honesto de "pouco movimento" com os dados disponíveis.
    const leastActiveVehicles = useMemo(() => {
        return [...vehicleValues]
            .map((v, index) => ({
                id: `least-${index}`,
                name: v.vehicle_name,
                products: parseBrl(v.total_products_value),
                services: parseBrl(v.total_services_value),
            }))
            .sort((a, b) => (a.products + a.services) - (b.products + b.services))
            .slice(0, 5);
    }, [vehicleValues]);

    const manufacturerOptions = useMemo(() => {
        const set = new Set(vehicleList.map((v) => v.Manufacturer).filter(Boolean));
        return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
    }, [vehicleList]);

    // Filtros Marca/Busca ficam dentro da lista "Frota cadastrada" (aba
    // Detalhado) — é a única tabela com uma linha por veículo. Os
    // KPIs/gráficos das outras abas são agregados que a própria API já
    // devolve prontos (e `/chart/vehicle` não aceita filtro por marca/busca).
    const filteredVehicleList = useMemo(() => {
        let list = vehicleList;
        if (manufacturerFilter) {
            list = list.filter((v) => v.Manufacturer === manufacturerFilter);
        }
        const term = normalizeText(search);
        if (term) {
            list = list.filter((v) => normalizeText(`${v.LicensePlate || ''} ${v.Model || ''} ${v.Manufacturer || ''} ${v.Customer || ''}`).includes(term));
        }
        return list;
    }, [vehicleList, manufacturerFilter, search]);

    const top5MostServices = (extraStats?.cars_with_most_services || []).slice(0, 5);
    const top5MostExpensive = (extraStats?.cars_with_most_expensive_services || []).slice(0, 5);

    const handleTabChange = (tab) => setActiveTab(tab);

    const isMobileShell = useIsMobileShell();
    // Mobile: gera ao abrir e a cada troca de período/filtros (mesmas chamadas do botão "Gerar").
    const regenerate = useMobileRegenerate(fetchReportData, { enabled: isMobileShell });

    if (isMobileShell) {
        const averageKm = Number(extraStats?.average_kilometer ?? statistics.average_kilometer) || 0;
        const desktopKm = Number(statistics.average_kilometer) || 0;
        const ageText = `${Number(statistics.average_age || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} anos`;
        const segmentsOf = (data) => data.map((item) => ({ value: item.value, color: item.color }));
        const legendOf = (data) => data.map((item) => ({ label: `${item.label} · ${Number(item.value).toLocaleString('pt-BR')}`, color: item.color }));
        const growthItems = monthlyGrowthData.map((r) => ({ label: r.month, value: Number(r.total) || 0 }));
        const peakGrowth = growthItems.reduce((best, it) => (it.value > (best?.value ?? -1) ? it : best), null);
        const barItems = (list) => list.map((d) => ({ name: d.label, value: Number(d.count) || 0, text: String(d.count) }));
        const canView = hasPermission('vehicle.view');

        const fleetRows = filteredVehicleList.map((row) => ({
            key: row.ID,
            title: `${row.Manufacturer || ''} ${row.Model || ''}`.trim() || '—',
            sub: [row.LicensePlate || '—', row.Customer || '—', fmtYear(row)].join(' · '),
            end: (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                    <KitPill tone="success">Ativo</KitPill>
                    <Box sx={{ fontSize: 11.5, color: 'text.disabled', lineHeight: 1.4 }}>{fmtKm(row.Kilometer)}</Box>
                </Box>
            ),
            onClick: canView ? () => handleViewVehicle(row) : undefined,
        }));
        const leastRows = leastActiveVehicles.map((row) => ({
            key: row.id,
            title: row.name,
            sub: `Produtos ${formatBrl(row.products)} · Serviços ${formatBrl(row.services)}`,
        }));

        const tabNode = activeTab === 0 ? (
            <>
                <ChartCard title="Status dos Veículos" subtitle="Situação da frota cadastrada" bare>
                    <StatTiles
                        items={[
                            { label: 'Total', value: totalVehicles.toLocaleString('pt-BR') },
                            { label: 'Ativos', value: activeVehicles.toLocaleString('pt-BR') },
                            { label: 'Inativos', value: inactiveVehicles.toLocaleString('pt-BR') },
                        ]}
                    />
                </ChartCard>
                <ChartCard title="Perfil da Frota" subtitle="Características predominantes" bare>
                    <KvCard
                        rows={[
                            { label: 'Marca mais comum', value: mostPopularManufacturerPct != null ? `${statistics.most_popular_manufacturer} (${mostPopularManufacturerPct}%)` : (statistics.most_popular_manufacturer || 'N/A') },
                            { label: 'Cor mais popular', value: statistics.most_popular_color || 'N/A' },
                            { label: 'Km médio', value: fmtKm(desktopKm) },
                            { label: 'Idade média', value: ageText },
                        ]}
                    />
                </ChartCard>
                <ChartCard title="Veículos com menor movimentação" subtitle="Menor valor de produtos/serviços entre os veículos atendidos" bare>
                    <PagedRows paged={false} rows={leastRows} empty="Nenhum veículo com produtos/serviços lançados em OS" />
                </ChartCard>
            </>
        ) : activeTab === 1 ? (
            <>
                <ChartCard title="Frota cadastrada" bare>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '10px', mb: '12px' }}>
                        <KitSelect aria-label="Marca" value={manufacturerFilter} onChange={(e) => setManufacturerFilter(e.target.value)}>
                            <option value="">Marca: Todas</option>
                            {manufacturerOptions.map((name) => <option key={name} value={name}>{name}</option>)}
                        </KitSelect>
                        <KitSearch placeholder="Placa, modelo ou cliente" value={search} onChange={(e) => setSearch(e.target.value)} />
                    </Box>
                    <PagedRows key={`${filteredVehicleList.length}-${manufacturerFilter}-${search}`} rows={fleetRows} empty="Nenhum veículo encontrado" noun="veículos" />
                </ChartCard>
            </>
        ) : (
            <>
                <ChartCard title="Distribuição por tipo" subtitle="Percentual da frota cadastrada" bare>
                    <DonutBlock segments={segmentsOf(typeChartData)} center={totalVehicles.toLocaleString('pt-BR')} legend={legendOf(typeChartData)} empty="Sem veículos cadastrados" />
                </ChartCard>
                <ChartCard title="Crescimento da frota cadastrada" subtitle={`Novos veículos por mês em ${dayjs().year()}`}>
                    {growthItems.some((it) => it.value > 0) ? (
                        <>
                            <AreaLine items={growthItems} />
                            {peakGrowth && peakGrowth.value > 0 && (
                                <Box sx={{ fontSize: 12, color: 'text.secondary', lineHeight: 1.4, mt: '10px' }}>
                                    Maior mês: <strong>{peakGrowth.label}</strong> · {peakGrowth.value.toLocaleString('pt-BR')} veículo(s)
                                </Box>
                            )}
                        </>
                    ) : <KitEmptyNote>Sem veículos cadastrados no ano</KitEmptyNote>}
                </ChartCard>
                <ChartCard title="Ativos x Inativos" bare>
                    <DonutBlock segments={segmentsOf(statusChartData)} center={`${activePct}%`} legend={legendOf(statusChartData)} empty="Sem veículos cadastrados" />
                </ChartCard>
                <ChartCard title="Top marcas da frota">
                    {topManufacturersBarData.length > 0 ? <HBarsLimited items={barItems(topManufacturersBarData)} valueWidth={40} /> : <KitEmptyNote>Sem veículos cadastrados</KitEmptyNote>}
                </ChartCard>
                <ChartCard title="Veículos por cor">
                    {colorChartBarData.length > 0 ? <HBarsLimited items={barItems(colorChartBarData)} valueWidth={40} /> : <KitEmptyNote>Sem veículos cadastrados</KitEmptyNote>}
                </ChartCard>
                <ChartCard title="Veículos com mais serviços" subtitle="Top 5 no período — só considera carros" bare>
                    <PagedRows
                        paged={false}
                        rows={top5MostServices.map((row, i) => ({ key: `svc-${i}`, avatar: { text: String(i + 1), tone: 'soft' }, title: row.vehicle_name, sub: row.license_plate || '—', end: <RowValue>{row.service_count} serv.</RowValue> }))}
                        empty="Sem serviços registrados em carros"
                    />
                </ChartCard>
                <ChartCard title="Veículos com serviços mais caros" subtitle="Top 5 por valor total — só considera carros" bare>
                    <PagedRows
                        paged={false}
                        rows={top5MostExpensive.map((row, i) => ({ key: `exp-${i}`, avatar: { text: String(i + 1), tone: 'soft' }, title: row.vehicle_name, sub: row.license_plate || '—', end: <RowValue color="success.main">{row.total_value}</RowValue> }))}
                        empty="Sem serviços registrados em carros"
                    />
                </ChartCard>
            </>
        );

        const tabsNode = (
            <Box sx={{ mt: '22px' }}>
                <KitSeg
                    value={activeTab}
                    onChange={setActiveTab}
                    options={[{ value: 0, label: 'Visão geral' }, { value: 1, label: 'Detalhado' }, { value: 2, label: 'Gráficos' }]}
                />
                <Box sx={{ mt: '14px' }}>{tabNode}</Box>
            </Box>
        );

        return (
            <PageScaffold title="Relatório de Veículos" titlePageTarget="Veículos" links={[{ title: 'Relatórios', link: '/reports' }]}>
                <MobileReport
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    onGenerate={regenerate}
                    onClear={handleClearFilters}
                >
                    <MobileReportBody
                        loading={loading}
                        ready={Boolean(reportData)}
                        kpis={[
                            { label: 'Veículos', icon: 'car', value: totalVehicles.toLocaleString('pt-BR') },
                            { label: 'Atendidos', icon: 'orders', value: attendedVehiclesCount.toLocaleString('pt-BR') },
                            { label: 'Média KM', icon: 'gauge', value: averageKm >= 1000 ? `${Math.round(averageKm / 1000).toLocaleString('pt-BR')} mil` : averageKm.toLocaleString('pt-BR') },
                            { label: 'Revisões vencidas', icon: 'alert', value: '—', delta: { text: '', tone: 'down' } },
                        ]}
                        sections={[
                            {
                                type: 'node',
                                title: 'Indicadores gerais da frota',
                                node: (
                                    <>
                                        <SecH title="Indicadores gerais da frota" />
                                        <KvCard
                                            rows={[
                                                { label: 'Veículos ativos', value: `${activeVehicles.toLocaleString('pt-BR')} (${activePct}%)` },
                                                { label: 'Marca mais comum', value: statistics.most_popular_manufacturer || 'N/A' },
                                                { label: 'Km médio da frota', value: fmtKm(desktopKm) },
                                                { label: 'Idade média da frota', value: ageText },
                                                { label: 'Ticket médio de OS por veículo', value: formatBrl(averageTicketPerVehicle) },
                                            ]}
                                        />
                                    </>
                                ),
                            },
                            {
                                type: 'hbars',
                                title: 'Por montadora',
                                items: manufacturerChart.slice(0, 4).map((item) => ({ name: item.label, value: item.value, text: String(item.value) })),
                                empty: 'Nenhum veículo cadastrado',
                            },
                            { type: 'node', title: 'Detalhamento', node: tabsNode },
                        ]}
                    />
                </MobileReport>
                <DialogInfo open={openDialog} onClose={() => setOpenDialog(false)} title="Erro" iconType="error" content={dialogMessage} />
                <VehicleViewDrawer open={openViewDrawer} vehicleId={viewVehicleId} onClose={() => setOpenViewDrawer(false)} />
            </PageScaffold>
        );
    }

    return (
        <PageScaffold
            title="Relatório de Veículos"
            titlePageTarget="Veículos"
            links={[{ title: "Relatórios", link: "/reports" }]}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <Box sx={{ width: '100%', maxWidth: 1400, mx: 'auto' }}>
                    {/* page-head — ícone + título + descrição + ação primária (Exportar CSV). */}
                    <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'flex-end' }, justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ width: 38, height: 38, borderRadius: '11px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', background: (t) => t.palette.gradient.brand }}>
                                <Car size={18} strokeWidth={2} />
                            </Box>
                            <Box>
                                <Typography component="h1" data-page-title sx={{ fontSize: '22px', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.3 }}>
                                    Relatório de Veículos
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: 'text.secondary', mt: 0.5 }}>
                                    Perfil da frota de clientes cadastrada e o volume de ordens de serviço gerado por veículo.
                                </Typography>
                            </Box>
                        </Box>

                        {/* <Button variant="contained" startIcon={<Download size={16} strokeWidth={2} />} onClick={handleExport}>
                            Exportar CSV
                        </Button> */}
                    </Box>

                    {/* Faixa de filtros — período. Marca/Busca ficam na lista da aba Detalhado. */}
                    <ReportFilterBar
                        start={startDate}
                        end={endDate}
                        onPeriodChange={(s, e) => { setStartDate(s); setEndDate(e); }}
                        onGenerate={fetchReportData}
                        loading={loading}
                        onClear={handleClearFilters}
                    />

                    {reportData ? (
                        <>
                            {/* seg-tabs — DESIGN-SYSTEM.md §5.4. */}
                            <Stack
                                direction="row"
                                sx={{ width: 'fit-content', p: '4px', gap: '4px', borderRadius: '11px', bgcolor: 'background.subtle', border: '1px solid', borderColor: 'divider', mb: 2.25, flexWrap: 'wrap' }}
                            >
                                {[
                                    { icon: LayoutGrid, label: 'Visão Geral' },
                                    { icon: Table2, label: 'Detalhado' },
                                    { icon: BarChart3, label: 'Gráficos' },
                                ].map((tab, index) => {
                                    const Icon = tab.icon;
                                    const selected = activeTab === index;
                                    return (
                                        <Button
                                            key={tab.label}
                                            onClick={() => handleTabChange(index)}
                                            startIcon={<Icon size={14} strokeWidth={2} />}
                                            sx={{
                                                gap: '7px',
                                                padding: '9px 18px',
                                                lineHeight: 'normal',
                                                borderRadius: '8px',
                                                fontSize: '12.5px',
                                                fontWeight: 700,
                                                textTransform: 'none',
                                                color: selected ? 'primary.main' : 'text.secondary',
                                                bgcolor: selected ? 'background.paper' : 'transparent',
                                                boxShadow: selected ? (t) => t.customShadows?.sm : 'none',
                                                '&:hover': { bgcolor: selected ? 'background.paper' : 'background.subtle' },
                                            }}
                                        >
                                            {tab.label}
                                        </Button>
                                    );
                                })}
                            </Stack>

                            {/* ===== TAB: VISÃO GERAL ===== */}
                            {activeTab === 0 && (
                                <Stack spacing={2}>
                                    {/* KPI grid principal — DESIGN-SYSTEM.md §5.2/§6.1. */}
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '2fr 1fr 1fr' }, gap: { xs: 1, md: '14px' } }}>
                                        <FleetHero
                                            total={totalVehicles}
                                            active={activeVehicles}
                                            inactive={inactiveVehicles}
                                            activePct={activePct}
                                            growth={monthlyGrowthData.map((r) => Number(r.total) || 0)}
                                            loading={loading}
                                        />
                                        <UsageKpi icon={<Wrench size={17} strokeWidth={2} />} iconBg="secondary.bg" iconFg="secondary.main" tag="Uso" value={<AnimatedNumber value={attendedVehiclesCount} />} label="Veículos atendidos (com OS)" hint="Veículos ativos com item de OS lançado" loading={loading} />
                                        <UsageKpi icon={<Factory size={17} strokeWidth={2} />} iconBg="warning.bg" iconFg="warning.main" tag="Perfil" value={statistics.most_popular_manufacturer || 'N/A'} label="Marca mais comum" hint={mostPopularManufacturerPct != null ? `${mostPopularManufacturerPct}% da frota` : 'Marca predominante da frota'} loading={loading} />
                                    </Box>

                                    {/* Indicadores gerais da frota (`.kv-strip`). */}
                                    <Box>
                                        <Typography sx={{ fontSize: '12px', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, m: '2px 0 10px' }}>
                                            Indicadores gerais da frota
                                        </Typography>
                                        <Card sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' } }}>
                                            <KvStripItem icon={<Gauge size={17} strokeWidth={2} />} iconBg="info.bg" iconFg="brand2.main" value={<AnimatedNumber value={Number(statistics.average_kilometer) || 0} format={fmtKm} />} label="Km médio da frota" loading={loading} />
                                            <KvStripItem icon={<Clock3 size={17} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" value={<AnimatedNumber value={Number(statistics.average_age || 0)} decimals={1} format={(n) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} anos`} />} label="Idade média da frota" loading={loading} />
                                            <KvStripItem icon={<Wallet size={17} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" value={<AnimatedNumber value={averageTicketPerVehicle} decimals={2} format={formatBrl} />} label="Ticket médio de OS por veículo" loading={loading} />
                                        </Card>
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1.4fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <Card>
                                            <CardContent sx={{ p: 2.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 2 }}>
                                                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'info.bg', color: 'primary.main', flexShrink: 0 }}>
                                                        <Car size={18} strokeWidth={2} />
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>Status dos Veículos</Typography>
                                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>Situação da frota cadastrada</Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
                                                    <StatTile icon={<Car size={20} strokeWidth={2} />} iconBg="info.bg" iconFg="primary.main" value={<AnimatedNumber value={totalVehicles} format={String} />} label="Total de veículos" loading={loading} />
                                                    <StatTile icon={<CheckCircle2 size={20} strokeWidth={2} />} iconBg="success.bg" iconFg="success.main" value={<AnimatedNumber value={activeVehicles} format={String} />} label="Veículos ativos" loading={loading} />
                                                    <StatTile icon={<XCircle size={20} strokeWidth={2} />} iconBg="error.bg" iconFg="error.main" value={<AnimatedNumber value={inactiveVehicles} format={String} />} label="Veículos inativos" loading={loading} />
                                                </Box>
                                            </CardContent>
                                        </Card>

                                        <Card>
                                            <CardContent sx={{ p: 2.25 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1 }}>
                                                    <Box sx={{ width: 36, height: 36, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'secondary.bg', color: 'secondary.main', flexShrink: 0 }}>
                                                        <BarChart3 size={18} strokeWidth={2} />
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>Perfil da Frota</Typography>
                                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>Características predominantes</Typography>
                                                    </Box>
                                                </Box>
                                                <MetricRow
                                                    icon={<Factory size={15} strokeWidth={2} />}
                                                    iconBg="warning.bg"
                                                    iconFg="warning.main"
                                                    label="Marca mais comum"
                                                    value={mostPopularManufacturerPct != null ? `${statistics.most_popular_manufacturer} (${mostPopularManufacturerPct}%)` : (statistics.most_popular_manufacturer || 'N/A')}
                                                />
                                                <MetricRow
                                                    icon={<Palette size={15} strokeWidth={2} />}
                                                    iconBg="secondary.bg"
                                                    iconFg="secondary.main"
                                                    label="Cor mais popular"
                                                    value={statistics.most_popular_color || 'N/A'}
                                                />
                                                <MetricRow
                                                    icon={<Gauge size={15} strokeWidth={2} />}
                                                    iconBg="info.bg"
                                                    iconFg="brand2.main"
                                                    label="Km médio"
                                                    value={<AnimatedNumber value={Number(statistics.average_kilometer) || 0} format={fmtKm} />}
                                                />
                                                <MetricRow
                                                    icon={<Clock3 size={15} strokeWidth={2} />}
                                                    iconBg="error.bg"
                                                    iconFg="error.main"
                                                    label="Idade média"
                                                    value={<AnimatedNumber value={Number(statistics.average_age || 0)} decimals={1} format={(n) => `${n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} anos`} />}
                                                />
                                            </CardContent>
                                        </Card>
                                    </Box>

                                    <Card>
                                        <CardContent sx={{ p: 2.25 }}>
                                            <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>Veículos com menor movimentação</Typography>
                                            <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px', mb: 2 }}>
                                                Menor valor de produtos/serviços entre os veículos atendidos — a API não expõe data da última OS por veículo hoje, então esta lista substitui o critério de "dias sem visita" do protótipo
                                            </Typography>
                                            <MiniTable
                                                maxHeight={320}
                                                columns={[
                                                    { key: 'name', label: 'Veículo' },
                                                    { key: 'products', label: 'Valor produtos', align: 'right' },
                                                    { key: 'services', label: 'Valor serviços', align: 'right' },
                                                ]}
                                                rows={leastActiveVehicles}
                                                emptyTitle="Nenhum veículo atendido"
                                                emptySubtitle="Nenhum veículo com produtos/serviços lançados em OS"
                                                renderRow={(row) => (
                                                    <TableRow key={row.id} hover>
                                                        <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.name}</TableCell>
                                                        <TableCell align="right" sx={miniBodyCellSx}>{formatBrl(row.products)}</TableCell>
                                                        <TableCell align="right" sx={miniBodyCellSx}>{formatBrl(row.services)}</TableCell>
                                                    </TableRow>
                                                )}
                                            />
                                        </CardContent>
                                    </Card>
                                </Stack>
                            )}

                            {/* ===== TAB: DETALHADO ===== */}
                            {activeTab === 1 && (
                                <Card>
                                    <CardContent sx={{ p: 2.25 }}>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700, color: 'text.primary' }}>Frota cadastrada</Typography>
                                        {/* <Typography sx={{ fontSize: '11px', color: 'text.disabled', mt: '2px', mb: 2 }}>
                                            {'GET /vehicles retorna somente veículos ativos — sem contagem de OS/última OS por veículo hoje (ver comentário no topo do arquivo)'}
                                        </Typography> */}
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, mt: 1.5, mb: 2 }}>
                                            <Box>
                                                <TbLabel>Marca</TbLabel>
                                                <FormControl size="small" sx={{ minWidth: 150 }}>
                                                    <Select
                                                        value={manufacturerFilter}
                                                        displayEmpty
                                                        onChange={(e) => setManufacturerFilter(e.target.value)}
                                                        renderValue={(value) => value || 'Todas'}
                                                        sx={selectSx}
                                                    >
                                                        <MenuItem value="">Todas</MenuItem>
                                                        {manufacturerOptions.map((name) => (
                                                            <MenuItem key={name} value={name}>{name}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </Box>

                                            <Box>
                                                <TbLabel>Buscar</TbLabel>
                                                <TextField
                                                    size="small"
                                                    placeholder="Placa, modelo ou cliente"
                                                    value={search}
                                                    onChange={(e) => setSearch(e.target.value)}
                                                    sx={textFieldSx}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Search size={15} strokeWidth={2} color={theme.palette.text.disabled} />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                        <DefaultTable
                                            colunms={[
                                                {
                                                    path: 'Model',
                                                    name: 'Veículo',
                                                    render: (row) => (
                                                        <Box>
                                                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: 'text.primary' }}>{`${row.Manufacturer || ''} ${row.Model || ''}`.trim() || '—'}</Typography>
                                                            <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>{row.Type ? vehicleTypeLabel(row.Type) : '—'}</Typography>
                                                        </Box>
                                                    ),
                                                },
                                                { path: 'LicensePlate', name: 'Placa', render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.LicensePlate || '—'}</Typography> },
                                                { path: 'Customer', name: 'Cliente', render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{row.Customer || '—'}</Typography> },
                                                { path: 'ModYear', name: 'Ano', render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{fmtYear(row)}</Typography> },
                                                { path: 'Kilometer', name: 'Km atual', render: (row) => <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>{fmtKm(row.Kilometer)}</Typography> },
                                                {
                                                    path: 'status',
                                                    name: 'Status',
                                                    // Sempre "Ativo": `GET /vehicles` só retorna veículos ativos
                                                    // (limitação documentada no topo do arquivo).
                                                    render: () => <Pill label="Ativo" tone="green" />,
                                                },
                                            ]}
                                            data={filteredVehicleList}
                                            loading={loading}
                                            rowPerPage={10}
                                            rowsPerPageOptions={[5, 10, 25]}
                                            itemLabel="veículos"
                                            iconsActions={hasPermission('vehicle.view') ? [{
                                                icon: <Eye size={16} strokeWidth={2} />,
                                                props: { onClick: handleViewVehicle, tooltip: 'Ver veículo', style: { cursor: 'pointer' } },
                                            }] : []}
                                            sx={{ boxShadow: 'none', border: 'none' }}
                                        />
                                    </CardContent>
                                </Card>
                            )}

                            {/* ===== TAB: GRÁFICOS ===== */}
                            {activeTab === 2 && (
                                <Stack spacing={2}>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <DonutPanel
                                            title="Distribuição por tipo"
                                            subtitle="Percentual da frota cadastrada"
                                            data={typeChartData}
                                            centerValue={totalVehicles.toLocaleString('pt-BR')}
                                            centerCaption="veículos"
                                            loading={loading}
                                            emptyTitle="Nenhum dado disponível"
                                            emptySubtitle="Sem veículos cadastrados"
                                        />
                                        <AreaLineChart
                                            dataset={monthlyGrowthData}
                                            xKey="month"
                                            yKey="total"
                                            gradientId="vehicleReportGrowthGradient"
                                            title="Crescimento da frota cadastrada"
                                            subtitle={`Novos veículos por mês em ${dayjs().year()}`}
                                            loading={loading}
                                            valueSuffix=" veículo(s)"
                                        />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' }, gap: 2, alignItems: 'stretch' }}>
                                        <DonutPanel
                                            title="Ativos x Inativos"
                                            data={statusChartData}
                                            centerValue={`${activePct}%`}
                                            centerCaption="ativos"
                                            loading={loading}
                                            emptyTitle="Nenhum dado disponível"
                                            emptySubtitle="Sem veículos cadastrados"
                                        />
                                        <CategoryBarChart data={topManufacturersBarData} xKey="label" title="Top marcas da frota" loading={loading} />
                                        {/* Substitui "OS por veículo (faixas)" do protótipo — ver
                                            comentário de cobertura de dados no topo do arquivo. */}
                                        <CategoryBarChart data={colorChartBarData} xKey="label" title="Veículos por cor" loading={loading} />
                                    </Box>

                                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'stretch' }}>
                                        <ChartPanel
                                            title="Veículos com mais serviços"
                                            subtitle="Top 5 no período — só considera carros (vehicle_type = 'car')"
                                            loading={loading}
                                        >
                                            <MiniTable
                                                columns={[{ key: 'vehicle', label: 'Veículo' }, { key: 'plate', label: 'Placa' }, { key: 'count', label: 'Serviços', align: 'right' }]}
                                                rows={top5MostServices}
                                                emptyTitle="Nenhum dado disponível"
                                                emptySubtitle="Sem serviços registrados em carros"
                                                renderRow={(row, index) => (
                                                    <TableRow key={`svc-${index}`} hover>
                                                        <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.vehicle_name}</TableCell>
                                                        <TableCell sx={miniBodyCellSx}>{row.license_plate || '—'}</TableCell>
                                                        <TableCell align="right" sx={miniBodyCellSx}><CountChip value={row.service_count} /></TableCell>
                                                    </TableRow>
                                                )}
                                            />
                                        </ChartPanel>
                                        <ChartPanel
                                            title="Veículos com serviços mais caros"
                                            subtitle="Top 5 por valor total — só considera carros (vehicle_type = 'car')"
                                            loading={loading}
                                        >
                                            <MiniTable
                                                columns={[{ key: 'vehicle', label: 'Veículo' }, { key: 'plate', label: 'Placa' }, { key: 'value', label: 'Valor total', align: 'right' }]}
                                                rows={top5MostExpensive}
                                                emptyTitle="Nenhum dado disponível"
                                                emptySubtitle="Sem serviços registrados em carros"
                                                renderRow={(row, index) => (
                                                    <TableRow key={`exp-${index}`} hover>
                                                        <TableCell sx={{ ...miniBodyCellSx, fontWeight: 700, color: 'text.primary' }}>{row.vehicle_name}</TableCell>
                                                        <TableCell sx={miniBodyCellSx}>{row.license_plate || '—'}</TableCell>
                                                        <TableCell align="right" sx={{ ...miniBodyCellSx, fontWeight: 800, color: 'success.main' }}>{row.total_value}</TableCell>
                                                    </TableRow>
                                                )}
                                            />
                                        </ChartPanel>
                                    </Box>
                                </Stack>
                            )}
                        </>
                    ) : (
                        <Card>
                            <CardContent sx={{ py: 7, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1.5 }}>
                                <Box sx={{ width: 72, height: 72, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.disabled' }}>
                                    <Car size={30} strokeWidth={2} />
                                </Box>
                                <Typography sx={{ fontSize: '15px', fontWeight: 700, color: 'text.secondary' }}>Nenhum relatório gerado</Typography>
                                <Typography sx={{ fontSize: '12.5px', color: 'text.disabled', maxWidth: 380 }}>
                                    Selecione um período e clique em "Gerar relatório" para visualizar os dados de veículos.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </LocalizationProvider>

            <VehicleViewDrawer
                open={openViewDrawer}
                vehicleId={viewVehicleId}
                onClose={() => setOpenViewDrawer(false)}
            />

            <DialogInfo
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                title="Erro"
                iconType="error"
                content={dialogMessage}
            />
        </PageScaffold>
    );
}
